<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="isOpen" class="command-palette-overlay" @click="close">
        <Transition name="scale">
          <div v-if="isOpen" class="command-palette" @click.stop>
            <!-- Search Input -->
            <div class="command-search">
              <i class="pi pi-search search-icon"></i>
              <input
                ref="searchInput"
                v-model="searchQuery"
                type="text"
                class="search-input"
                :placeholder="t('commandPalette.placeholder')"
                @keydown="handleKeydown"
              />
              <kbd class="search-shortcut">ESC</kbd>
            </div>

            <!-- Results -->
            <div class="command-results">
              <!-- Recent Pages -->
              <div v-if="!searchQuery && recentPages.length > 0" class="command-section">
                <div class="section-header">
                  <span>{{ t('commandPalette.recent') }}</span>
                  <button class="clear-btn" @click="clearRecent">
                    {{ t('commandPalette.clear') }}
                  </button>
                </div>
                <div class="command-list">
                  <button
                    v-for="(page, index) in recentPages"
                    :key="page.path"
                    class="command-item"
                    :class="{ active: selectedIndex === index }"
                    @click="navigateTo(page.path, page.label, page.icon)"
                    @mouseenter="selectedIndex = index"
                  >
                    <i :class="[page.icon, 'item-icon']"></i>
                    <span class="item-label">{{ page.label }}</span>
                    <i class="pi pi-history item-hint"></i>
                  </button>
                </div>
              </div>

              <!-- Search Results or Default Categories -->
              <template v-if="searchQuery">
                <!-- Filtered Results -->
                <div v-if="filteredCommands.length > 0" class="command-section">
                  <div class="section-header">{{ t('commandPalette.results') }}</div>
                  <div class="command-list">
                    <button
                      v-for="(cmd, index) in filteredCommands"
                      :key="cmd.id"
                      class="command-item"
                      :class="{ active: selectedIndex === index + recentOffset }"
                      @click="executeCommand(cmd)"
                      @mouseenter="selectedIndex = index + recentOffset"
                    >
                      <i :class="[cmd.icon, 'item-icon']"></i>
                      <span class="item-label">{{ cmd.label }}</span>
                      <span class="item-category">{{ getCategoryLabel(cmd.category) }}</span>
                    </button>
                  </div>
                </div>
                <div v-else class="command-empty">
                  <i class="pi pi-search"></i>
                  <span>{{ t('commandPalette.noResults') }}</span>
                </div>
              </template>

              <template v-else>
                <!-- Actions -->
                <div class="command-section">
                  <div class="section-header">{{ t('commandPalette.actions') }}</div>
                  <div class="command-list">
                    <button
                      v-for="(cmd, index) in actionCommands"
                      :key="cmd.id"
                      class="command-item"
                      :class="{ active: selectedIndex === index + recentOffset }"
                      @click="executeCommand(cmd)"
                      @mouseenter="selectedIndex = index + recentOffset"
                    >
                      <i :class="[cmd.icon, 'item-icon action']"></i>
                      <span class="item-label">{{ cmd.label }}</span>
                    </button>
                  </div>
                </div>

                <!-- Pages -->
                <div class="command-section">
                  <div class="section-header">{{ t('commandPalette.pages') }}</div>
                  <div class="command-list">
                    <button
                      v-for="(cmd, index) in pageCommands.slice(0, 6)"
                      :key="cmd.id"
                      class="command-item"
                      :class="{
                        active: selectedIndex === index + recentOffset + actionCommands.length,
                      }"
                      @click="executeCommand(cmd)"
                      @mouseenter="selectedIndex = index + recentOffset + actionCommands.length"
                    >
                      <i :class="[cmd.icon, 'item-icon']"></i>
                      <span class="item-label">{{ cmd.label }}</span>
                    </button>
                  </div>
                </div>
              </template>
            </div>

            <!-- Footer -->
            <div class="command-footer">
              <div class="footer-hints">
                <span><kbd>↑↓</kbd> {{ t('commandPalette.navigate') }}</span>
                <span><kbd>↵</kbd> {{ t('commandPalette.select') }}</span>
                <span><kbd>esc</kbd> {{ t('commandPalette.close') }}</span>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useCommandPalette } from '@/composables/useCommandPalette'
import {
  searchCommands,
  getCommandsByCategory,
  filterCommandsByRole,
  type Command,
} from '@/config/commands'
import { useI18n } from '@/composables/useI18n'
import { useAuthStore } from '@/stores/auth'
import type { UserRole } from '@/types/permissions'

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()
const { isOpen, searchQuery, recentPages, close, clearRecent, addToRecent } = useCommandPalette()

const searchInput = ref<HTMLInputElement | null>(null)
const selectedIndex = ref(0)

// Get user role
const userRole = computed(() => authStore.user?.role as UserRole | undefined)

// Commands by category, filtered by role
const actionCommands = computed(() => {
  const actions = getCommandsByCategory('action')
  return filterCommandsByRole(actions, userRole.value)
})

const pageCommands = computed(() => {
  const pages = getCommandsByCategory('page')
  return filterCommandsByRole(pages, userRole.value)
})

// Search results, filtered by role
const filteredCommands = computed(() => {
  if (!searchQuery.value) return []
  const results = searchCommands(searchQuery.value)
  return filterCommandsByRole(results, userRole.value)
})

// Calculate offset for recent pages
const recentOffset = computed(() => {
  if (searchQuery.value) return 0
  return recentPages.value.length
})

// Total items count for navigation
const totalItems = computed(() => {
  if (searchQuery.value) {
    return filteredCommands.value.length
  }
  return (
    recentPages.value.length + actionCommands.value.length + Math.min(6, pageCommands.value.length)
  )
})

// Focus input when opened
watch(isOpen, async (open) => {
  if (open) {
    selectedIndex.value = 0
    await nextTick()
    searchInput.value?.focus()
  }
})

// Reset selection when search changes
watch(searchQuery, () => {
  selectedIndex.value = 0
})

const handleKeydown = (e: KeyboardEvent) => {
  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault()
      selectedIndex.value = (selectedIndex.value + 1) % totalItems.value
      break
    case 'ArrowUp':
      e.preventDefault()
      selectedIndex.value = (selectedIndex.value - 1 + totalItems.value) % totalItems.value
      break
    case 'Enter':
      e.preventDefault()
      executeSelected()
      break
    case 'Escape':
      e.preventDefault()
      close()
      break
  }
}

const executeSelected = () => {
  if (searchQuery.value && filteredCommands.value.length > 0) {
    executeCommand(filteredCommands.value[selectedIndex.value])
    return
  }

  // Handle recent pages
  if (selectedIndex.value < recentPages.value.length) {
    const page = recentPages.value[selectedIndex.value]
    navigateTo(page.path, page.label, page.icon)
    return
  }

  // Handle actions
  const actionIndex = selectedIndex.value - recentPages.value.length
  if (actionIndex < actionCommands.value.length) {
    executeCommand(actionCommands.value[actionIndex])
    return
  }

  // Handle pages
  const pageIndex = actionIndex - actionCommands.value.length
  if (pageIndex < pageCommands.value.length) {
    executeCommand(pageCommands.value[pageIndex])
  }
}

const executeCommand = (cmd: Command) => {
  if (cmd.path) {
    navigateTo(cmd.path, cmd.label, cmd.icon)
  } else if (cmd.action) {
    cmd.action()
    close()
  }
}

const navigateTo = (path: string, label: string, icon: string) => {
  addToRecent({ path, label, icon })
  router.push(path)
  close()
}

const getCategoryLabel = (category: Command['category']): string => {
  switch (category) {
    case 'page':
      return t('commandPalette.page')
    case 'action':
      return t('commandPalette.action')
    case 'setting':
      return t('commandPalette.setting')
    default:
      return ''
  }
}
</script>

<style scoped>
.command-palette-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 15vh;
}

.command-palette {
  width: 100%;
  max-width: 560px;
  background: var(--app-surface-0, #fff);
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  border: 1px solid var(--app-border);
}

.app-dark .command-palette {
  background: var(--app-surface-900, #1a1a1a);
  border-color: var(--app-surface-700);
}

/* Search */
.command-search {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--app-border);
}

.search-icon {
  font-size: 1.125rem;
  color: var(--app-text-muted);
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 1rem;
  color: var(--app-text-primary);
  outline: none;
}

.search-input::placeholder {
  color: var(--app-text-muted);
}

.app-dark .search-input {
  color: var(--app-surface-0);
}

.search-shortcut {
  padding: 4px 8px;
  font-size: 0.75rem;
  font-family: inherit;
  background: var(--app-surface-100);
  border: 1px solid var(--app-border);
  border-radius: 4px;
  color: var(--app-text-muted);
}

.app-dark .search-shortcut {
  background: var(--app-surface-800);
  border-color: var(--app-surface-700);
}

/* Results */
.command-results {
  max-height: 400px;
  overflow-y: auto;
  padding: 8px;
}

.command-section {
  margin-bottom: 8px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px 4px;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--app-text-muted);
}

.clear-btn {
  padding: 2px 6px;
  font-size: 0.625rem;
  background: transparent;
  border: none;
  color: var(--app-text-muted);
  cursor: pointer;
  border-radius: 4px;
}

.clear-btn:hover {
  background: var(--app-surface-100);
  color: var(--app-text-primary);
}

.app-dark .clear-btn:hover {
  background: var(--app-surface-800);
  color: var(--app-surface-0);
}

.command-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.command-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
}

.command-item:hover,
.command-item.active {
  background: var(--app-surface-100);
}

.app-dark .command-item:hover,
.app-dark .command-item.active {
  background: var(--app-surface-800);
}

.item-icon {
  font-size: 1rem;
  width: 1.25rem;
  text-align: center;
  color: var(--app-text-secondary);
}

.item-icon.action {
  color: var(--app-primary-text);
}

.item-label {
  flex: 1;
  font-size: 0.875rem;
  color: var(--app-text-primary);
}

.app-dark .item-label {
  color: var(--app-surface-0);
}

.item-category {
  font-size: 0.6875rem;
  color: var(--app-text-muted);
  text-transform: uppercase;
}

.item-hint {
  font-size: 0.75rem;
  color: var(--app-text-muted);
}

/* Empty state */
.command-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px;
  color: var(--app-text-muted);
}

.command-empty i {
  font-size: 1.5rem;
}

/* Footer */
.command-footer {
  padding: 12px 16px;
  border-top: 1px solid var(--app-border);
  background: var(--app-surface-50);
}

.app-dark .command-footer {
  background: var(--app-surface-800);
}

.footer-hints {
  display: flex;
  gap: 16px;
  font-size: 0.6875rem;
  color: var(--app-text-muted);
}

.footer-hints kbd {
  padding: 2px 6px;
  font-family: inherit;
  background: var(--app-surface-100);
  border: 1px solid var(--app-border);
  border-radius: 4px;
  margin-right: 4px;
}

.app-dark .footer-hints kbd {
  background: var(--app-surface-700);
  border-color: var(--app-surface-600);
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.scale-enter-active,
.scale-leave-active {
  transition: all 0.2s ease;
}

.scale-enter-from,
.scale-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-10px);
}
</style>
